import { calculateEstimatedCost, isInternationalShipping } from './GeoShippingEstimator';

/**
 * Calculate estimated return shipping cost based on distance, parcel size, and item details
 * Uses the same logic as ShippingOption.js for consistency
 * @param {Object} params - Parameters for cost calculation
 * @param {number} params.distanceInMeters - Distance in meters
 * @param {boolean} params.isInternational - Whether this is international shipping
 * @param {Array} params.items - Array of order items being returned
 * @param {Object} params.fromAddress - Return from address (customer)
 * @param {Object} params.toAddress - Return to address (merchant)
 * @returns {number} - Estimated return shipping cost
 */
export const calculateReturnShippingCost = ({
  distanceInMeters,
  isInternational,
  items = [],
  fromAddress = {},
  toAddress = {}
}) => {
  console.log('🚚 STARTING RETURN SHIPPING COST CALCULATION');
  console.log('📏 Distance:', (distanceInMeters / 1609.34).toFixed(2), 'miles');
  console.log('🌍 International:', isInternational);
  console.log('📦 Items:', items.length);

  // Base shipping rate (same as ShippingOption.js)
  let baseRate = 5.99;
  
  // International shipping has higher base rate
  if (isInternational) {
    baseRate = 19.99;
    console.log('🌍 Using international base rate:', baseRate);
  } else {
    console.log('🏠 Using domestic base rate:', baseRate);
  }
  
  // Distance in miles
  const distanceInMiles = distanceInMeters / 1609.34;
  
  // Simple calculation: base rate + additional per mile (same as ShippingOption.js)
  let cost = baseRate;
  
  if (isInternational) {
    // International: steeper increase per mile
    const distanceAddon = distanceInMiles * 0.015;
    cost += distanceAddon;
    console.log('🌍 International distance addon:', distanceAddon.toFixed(2));
  } else {
    // Domestic: only add cost after first 20 miles - FIXED TO USE CORRECT RATE
    if (distanceInMiles > 20) {
      const distanceAddon = (distanceInMiles - 20) * 0.01; // FIXED: Was 0.10, now 0.01 like ShippingOption.js
      cost += distanceAddon;
      console.log('🏠 Domestic distance addon (after 20 miles):', distanceAddon.toFixed(2));
    } else {
      console.log('🏠 Distance under 20 miles, no distance addon');
    }
  }
  
  console.log('💰 Cost before parcel size adjustment:', cost.toFixed(2));
  
  // Calculate parcel size multiplier for fashion items
  const sizeMultiplier = calculateParcelSizeMultiplier(items);
  console.log('📏 Parcel size multiplier:', sizeMultiplier);
  
  // Apply size multiplier
  cost *= sizeMultiplier;
  console.log('💰 Cost after size multiplier:', cost.toFixed(2));
  
  // Cap at reasonable maximum (same as ShippingOption.js)
  const maxCost = isInternational ? 59.99 : 29.99;
  const finalCost = Math.min(cost, maxCost);
  
  if (finalCost === maxCost) {
    console.log('🚫 Cost capped at maximum:', maxCost);
  }
  
  console.log('✅ FINAL RETURN SHIPPING COST:', finalCost.toFixed(2));
  
  return Math.round(finalCost * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculate parcel size multiplier based on items being returned
 * Fashion items have different shipping characteristics but shouldn't drastically increase costs
 * @param {Array} items - Array of order items
 * @returns {number} - Size multiplier (1.0 = standard, higher = larger/heavier)
 */
const calculateParcelSizeMultiplier = (items = []) => {
  if (!items.length) return 1.0;
  
  console.log('📏 STARTING PARCEL SIZE CALCULATION for', items.length, 'items');
  
  let totalSizeScore = 0;
  let totalQuantity = 0;
  
  items.forEach((item, index) => {
    const quantity = item.quantity || 1;
    totalQuantity += quantity;
    
    // Fashion item size scoring based on product category/type
    let itemSizeScore = 1.0; // Default for most clothing
    
    // Get product details
    const productName = item.product?.name || item.name || '';
    const category = item.product?.category || item.category || '';
    const productLower = productName.toLowerCase();
    const categoryLower = category.toLowerCase();
    
    // Heavy/bulky items (moderate increase)
    if (productLower.includes('coat') || productLower.includes('jacket') || 
        productLower.includes('boot') || productLower.includes('shoes') ||
        categoryLower.includes('outerwear') || categoryLower.includes('footwear')) {
      itemSizeScore = 1.2; // Reduced from 1.4
      console.log(`📦 Item ${index + 1}: "${productName}" - HEAVY (${itemSizeScore}x)`);
    }
    // Light items (small decrease)
    else if (productLower.includes('jewelry') || productLower.includes('accessory') ||
             productLower.includes('scarf') || productLower.includes('belt') ||
             categoryLower.includes('accessories') || categoryLower.includes('jewelry')) {
      itemSizeScore = 0.8; // Reduced impact from 0.7
      console.log(`📦 Item ${index + 1}: "${productName}" - LIGHT (${itemSizeScore}x)`);
    }
    // Standard clothing items
    else {
      itemSizeScore = 1.0;
      console.log(`📦 Item ${index + 1}: "${productName}" - STANDARD (${itemSizeScore}x)`);
    }
    
    // Add quantity impact (much more conservative)
    const itemTotalScore = itemSizeScore * quantity;
    totalSizeScore += itemTotalScore;
    
    console.log(`   Quantity: ${quantity}, Item total score: ${itemTotalScore.toFixed(2)}`);
  });
  
  // Calculate average size factor with less dramatic scaling
  const averageSizeFactor = totalSizeScore / totalQuantity;
  console.log('📊 Average size factor:', averageSizeFactor.toFixed(2));
  
  // Apply quantity scaling (much more conservative)
  let quantityMultiplier = 1.0;
  if (totalQuantity > 1) {
    // Very small increase for multiple items (only 5% per additional item, capped at 30%)
    quantityMultiplier = Math.min(1.0 + (totalQuantity - 1) * 0.05, 1.3);
    console.log('📈 Quantity multiplier for', totalQuantity, 'items:', quantityMultiplier.toFixed(2));
  }
  
  // Final multiplier calculation (much more conservative)
  const finalMultiplier = averageSizeFactor * quantityMultiplier;
  
  // Cap the multiplier to prevent extreme costs (reduced caps)
  const cappedMultiplier = Math.min(Math.max(finalMultiplier, 0.8), 1.5); // Reduced from 0.5-2.0 to 0.8-1.5
  
  console.log('📊 PARCEL SIZE CALCULATION SUMMARY:', {
    itemCount: items.length,
    totalQuantity,
    averageSizeFactor: averageSizeFactor.toFixed(2),
    quantityMultiplier: quantityMultiplier.toFixed(2),
    finalMultiplier: finalMultiplier.toFixed(2),
    cappedMultiplier: cappedMultiplier.toFixed(2),
    wasCapped: finalMultiplier !== cappedMultiplier
  });
  
  return cappedMultiplier;
};

/**
 * Estimate return shipping for fulfillment group
 * @param {Object} fulfillmentGroup - Fulfillment group data
 * @param {Object} customerAddress - Customer's address for return shipping
 * @param {Object} merchantAddress - Merchant's return address
 * @returns {Promise<number>} - Estimated return shipping cost
 */
export const estimateReturnShippingForFG = async (fulfillmentGroup, customerAddress, merchantAddress) => {
  console.log('🏁 STARTING RETURN SHIPPING ESTIMATION');
  console.log('📍 CUSTOMER ADDRESS:', {
    city: customerAddress?.city,
    state: customerAddress?.state,
    country: customerAddress?.country,
    latitude: customerAddress?.latitude,
    longitude: customerAddress?.longitude
  });
  console.log('📍 MERCHANT ADDRESS:', {
    city: merchantAddress?.city,
    state: merchantAddress?.state,
    country: merchantAddress?.country,
    latitude: merchantAddress?.latitude,
    longitude: merchantAddress?.longitude
  });
  
  // Declare isInternational in outer scope so it's accessible in catch block
  let isInternational = false;
  
  try {
    // Get return items (items with return_requested_by_customer status)
    const returnItems = fulfillmentGroup.orderItems?.filter(
      item => item.status === 'return_requested_by_customer'
    ) || [];
    
    console.log('📦 RETURN ITEMS:', {
      totalOrderItems: fulfillmentGroup.orderItems?.length || 0,
      returnItemsCount: returnItems.length,
      returnItems: returnItems.map(item => ({
        id: item.id,
        title: item.product?.title,
        category: item.product?.category,
        price: item.price,
        quantity: item.quantity,
        status: item.status
      }))
    });
    
    if (!returnItems.length) {
      console.warn('⚠️ No return items found in fulfillment group');
      return 5.99; // Default minimum shipping
    }
    
    // Calculate distance if we have coordinates
    let distanceInMeters = 1000000; // Default 1000km for estimation
    let distanceCalculationMethod = 'default_fallback';
    
    if (customerAddress?.latitude && customerAddress?.longitude &&
        merchantAddress?.latitude && merchantAddress?.longitude) {
      
      console.log('📏 Calculating distance using coordinates...');
      
      // Use geolib to calculate distance
      const { getDistance } = await import('geolib');
      distanceInMeters = getDistance(
        { latitude: customerAddress.latitude, longitude: customerAddress.longitude },
        { latitude: merchantAddress.latitude, longitude: merchantAddress.longitude }
      );
      distanceCalculationMethod = 'coordinates';
      
      console.log('📏 DISTANCE CALCULATION:', {
        method: 'coordinates',
        distanceInMeters,
        distanceInMiles: (distanceInMeters / 1609.34).toFixed(2),
        distanceInKm: (distanceInMeters / 1000).toFixed(2)
      });
      
      // Check if international
      isInternational = isInternationalShipping(customerAddress, merchantAddress);
    } else {
      console.log('⚠️ Missing coordinates, using fallback distance');
      console.log('Customer coords available:', !!(customerAddress?.latitude && customerAddress?.longitude));
      console.log('Merchant coords available:', !!(merchantAddress?.latitude && merchantAddress?.longitude));
      
      // Fallback: check countries if coordinates not available
      if (customerAddress?.country && merchantAddress?.country) {
        isInternational = isInternationalShipping(customerAddress, merchantAddress);
        distanceCalculationMethod = 'country_fallback';
      }
    }
    
    console.log('🌎 INTERNATIONAL CHECK:', {
      isInternational,
      customerCountry: customerAddress?.country,
      merchantCountry: merchantAddress?.country,
      method: distanceCalculationMethod
    });
    
    const finalCost = calculateReturnShippingCost({
      distanceInMeters,
      isInternational,
      items: returnItems,
      fromAddress: customerAddress,
      toAddress: merchantAddress
    });
    
    console.log('✅ FINAL RETURN SHIPPING ESTIMATE:', `$${finalCost.toFixed(2)}`);
    return finalCost;
    
  } catch (error) {
    console.error('❌ Error estimating return shipping:', error);
    const fallbackCost = isInternational ? 19.99 : 5.99;
    console.log('🔄 Using fallback cost:', `$${fallbackCost.toFixed(2)}`);
    return fallbackCost; // Fallback costs
  }
}; 